/**
 * ProjectScheduleTab.jsx
 * =======================
 * Renders the locked multi-unit schedule for a project as a flat list with
 * MM/DD/YYYY dates. Hosts the "Edit / Re-lock" button that re-opens the
 * TransferWizard.
 *
 * Empty state: no schedule yet → shows "Generate schedule" CTA which opens
 * the wizard.
 *
 * Future home for: cascade emails status (Pending Confirm / Confirmed / Pushed),
 * per-row contractor avatars, drift indicators.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Loader2, Pencil, RefreshCw, Wand2, Send, CheckCircle2, AlertTriangle, Mail, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import TransferWizard from '@/components/TransferWizard';
import SendScheduleQuestionnaireModal from '@/components/SendScheduleQuestionnaireModal';
import ProjectSchedule from '@/pages/ProjectSchedule';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MM/DD/YYYY HH:MM (canonical for DrawReady — `toLocaleString` produces M/D/YYYY without leading zeros)
function fmtMDYDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(d)} ${hh}:${mm}`;
}

export default function ProjectScheduleTab({ project, user }) {
  // Default to the full task-based schedule (manual build + import + edit +
  // calendar + export). The wizard-driven crew cascade (alerts subs, asks for
  // confirmations, ripples pushed dates downstream) lives behind a toggle.
  const [mode, setMode] = useState('tasks');
  return (
    <div className="space-y-4">
      <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="schedule-mode-toggle">
        <button
          onClick={() => setMode('tasks')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${mode === 'tasks' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          data-testid="schedule-mode-tasks"
        >
          <Calendar className="w-4 h-4" /> Tasks &amp; Calendar
        </button>
        <button
          onClick={() => setMode('crew')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${mode === 'crew' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          data-testid="schedule-mode-crew"
        >
          <Send className="w-4 h-4" /> Crew Sync &amp; Alerts
        </button>
      </div>
      {mode === 'tasks'
        ? <ProjectSchedule embedded embeddedProjectId={project?.id} />
        : <CrewSyncSchedule project={project} user={user} />}
    </div>
  );
}

function CrewSyncSchedule({ project, user }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cascadeStatus, setCascadeStatus] = useState([]);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [cascadeSending, setCascadeSending] = useState(false);
  // v2.5.5.10 — awarded contractors that need timing data
  const [awarded, setAwarded] = useState([]);
  const [askModalOpen, setAskModalOpen] = useState(false);
  const [askInitialContractor, setAskInitialContractor] = useState('');
  const [askInitialTrades, setAskInitialTrades] = useState([]);

  const loadAwarded = useCallback(async () => {
    if (!project?.id || !user?.id) return;
    try {
      const r = await axios.get(`${API}/projects/${project.id}/awarded-contractors`, { params: { user_id: user.id } });
      setAwarded(r.data?.awarded || []);
    } catch (e) {
      // non-fatal
    }
  }, [project?.id, user?.id]);

  const askForDays = (row) => {
    setAskInitialContractor(row.contractor_id || '');
    setAskInitialTrades(row.trade_type ? [row.trade_type] : []);
    setAskModalOpen(true);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/schedule`);
      setSchedule(res.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setSchedule(null);
      } else {
        toast.error(e?.response?.data?.detail || 'Could not load schedule');
      }
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  const loadCascadeStatus = useCallback(async () => {
    if (!project?.id) return;
    setCascadeLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/schedule/cascade/status`);
      setCascadeStatus(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Non-blocking
    } finally {
      setCascadeLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); loadCascadeStatus(); loadAwarded(); }, [load, loadCascadeStatus, loadAwarded]);

  const sendCascade = async () => {
    if (!window.confirm(`Send schedule confirmation emails to all contractors on ${project.name}?`)) return;
    setCascadeSending(true);
    try {
      const res = await axios.post(`${API}/projects/${project.id}/schedule/cascade/send`);
      const { sent_count, skipped_count } = res.data;
      toast.success(`Sent ${sent_count} email${sent_count === 1 ? '' : 's'}${skipped_count ? ` · ${skipped_count} skipped` : ''}`);
      loadCascadeStatus();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cascade failed');
    } finally {
      setCascadeSending(false);
    }
  };

  // v2.5.5.10 — Awarded contractors panel: list of awarded contractors and
  // a per-row "Ask for days" button. Migration use case: contractor has an
  // award outside DrawReady, owner just needs timing to fill the schedule.
  const renderAwardedPanel = () => {
    if (!awarded || awarded.length === 0) return null;
    const missing = awarded.filter(a => !a.has_submitted_days);
    return (
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="awarded-contractors-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/70">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              Awarded contractors
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {missing.length > 0
                ? `${missing.length} of ${awarded.length} haven't submitted days yet.`
                : `All ${awarded.length} contractors have submitted timing.`}
            </p>
          </div>
          {missing.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setAskInitialContractor(''); setAskInitialTrades([]); setAskModalOpen(true); }}
              data-testid="ask-all-for-days-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Ask another
            </Button>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {awarded.map((row) => (
            <div key={row.bid_request_id} className="flex items-center gap-3 px-4 py-3" data-testid={`awarded-row-${row.contractor_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {row.contractor_name}
                  <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">
                    {row.trade_type || 'trade'}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {row.contractor_email || 'no email on file'} · {row.title || 'awarded bid'}
                </div>
              </div>
              {row.has_submitted_days ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" data-testid={`awarded-has-days-${row.contractor_id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Days received
                </span>
              ) : row.last_questionnaire_status === 'sent' ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => askForDays(row)}
                  data-testid={`ask-days-resend-${row.contractor_id}`}
                  className="text-xs"
                >
                  <Clock className="w-3.5 h-3.5 mr-1 text-amber-600" /> Resend
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => askForDays(row)}
                  data-testid={`ask-days-btn-${row.contractor_id}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                  disabled={!row.contractor_email}
                  title={row.contractor_email ? '' : 'Contractor has no email on file'}
                >
                  <Mail className="w-3.5 h-3.5 mr-1" /> Ask for days
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500" data-testid="schedule-tab-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading schedule…
      </div>
    );
  }

  // Empty state
  if (!schedule) {
    return (
      <div className="space-y-6" data-testid="schedule-tab-empty">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-emerald-600" /> Multi-Unit Schedule
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            No schedule locked yet for {project?.name}.
          </p>
        </div>

        <div className="rounded-xl border-2 border-dashed border-slate-300 p-12 text-center bg-white">
          <Wand2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <h3 className="text-base font-semibold text-slate-900 mb-1">Generate the project schedule</h3>
          <p className="text-sm text-slate-600 max-w-md mx-auto mb-4">
            The 5-step wizard takes contractor bid days and builds the staggered timeline.
            Per-building and per-unit modes are handled automatically.
          </p>
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="schedule-tab-generate-cta"
          >
            <Wand2 className="w-4 h-4 mr-2" /> Generate schedule
          </Button>
        </div>

        {/* Awarded contractor migration helper */}
        {renderAwardedPanel()}

        {wizardOpen && (
          <TransferWizard
            project={project}
            userId={user?.id}
            onClose={() => setWizardOpen(false)}
            onLocked={() => { /* user clicks Done */ load(); }}
          />
        )}

        <SendScheduleQuestionnaireModal
          open={askModalOpen}
          onOpenChange={(v) => { setAskModalOpen(v); if (!v) loadAwarded(); }}
          project={project}
          user={user}
          initialContractorId={askInitialContractor}
          initialTrades={askInitialTrades}
        />
      </div>
    );
  }

  // Locked state
  const bars = schedule.bars || [];
  const lockedDate = schedule.locked_at ? new Date(schedule.locked_at) : null;
  // Build phase_id → mode lookup so per_building bars show "Bldg N" and per_unit bars show "Unit N"
  const modeByPhase = Object.fromEntries((schedule.phases || []).map(p => [p.id, p.mode]));

  return (
    <div className="space-y-6" data-testid="schedule-tab-locked">
      {/* Header card */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-emerald-600" /> Multi-Unit Schedule
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          {schedule.bars?.length || 0} entries · {schedule.total_days} days · {' '}
          <span className="tabular-nums font-semibold">{formatDate(schedule.start_date)}</span>
          {' → '}
          <span className="tabular-nums font-semibold">{formatDate(schedule.end_date)}</span>
        </p>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">Layout</div>
          <div className="text-sm font-semibold text-slate-900">
            {schedule.building_count} building{schedule.building_count !== 1 ? 's' : ''} · {schedule.unit_count} unit{schedule.unit_count !== 1 ? 's' : ''}
            {schedule.units_per_building && schedule.building_count > 1 && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                ({schedule.units_per_building.map((n, i) => `B${i+1}: ${n}u`).join(' · ')})
              </span>
            )}
          </div>
        </div>
        {lockedDate && (
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">Locked</div>
            <div className="text-sm font-semibold text-slate-900 tabular-nums">
              {fmtMDYDateTime(schedule.locked_at)}
            </div>
          </div>
        )}
        <Button
          variant="outline"
          onClick={() => setWizardOpen(true)}
          data-testid="schedule-tab-edit"
          className="ml-auto"
        >
          <Pencil className="w-4 h-4 mr-2" /> Edit / Re-lock
        </Button>
        <Button
          variant="outline"
          onClick={load}
          data-testid="schedule-tab-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Flat list (matches wizard's Step 4 styling) */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50/70 border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          <div className="col-span-5">Phase</div>
          <div className="col-span-2">Scope</div>
          <div className="col-span-2 text-right tabular-nums">Start</div>
          <div className="col-span-2 text-right tabular-nums">End</div>
          <div className="col-span-1 text-right">Days</div>
        </div>
        <div className="divide-y divide-slate-100">
          {bars.map((b, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-slate-50"
              data-testid={`schedule-tab-row-${i}`}
            >
              <div className="col-span-5 text-sm font-medium text-slate-900 truncate">
                {b.phase_name}
              </div>
              <div className="col-span-2">
                {b.unit === 'all' ? (
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold">
                    All units
                  </span>
                ) : modeByPhase[b.phase_id] === 'per_building' ? (
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-semibold">
                    Bldg {b.unit}
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-semibold">
                    Unit {b.unit}
                  </span>
                )}
              </div>
              <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700">
                {formatDate(b.start_date)}
              </div>
              <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700">
                {formatDate(b.end_date)}
              </div>
              <div className="col-span-1 text-right text-sm font-bold text-slate-900">
                {b.duration_days}d
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cascade emails — send + per-contractor status */}
      <CascadeSection
        schedule={schedule}
        rows={cascadeStatus}
        loading={cascadeLoading}
        sending={cascadeSending}
        onSend={sendCascade}
        onRefresh={loadCascadeStatus}
      />

      {/* Awarded contractor migration helper */}
      {renderAwardedPanel()}

      {wizardOpen && (
        <TransferWizard
          project={project}
          userId={user?.id}
          onClose={() => setWizardOpen(false)}
          onLocked={() => { /* user clicks Done */ load(); }}
        />
      )}

      <SendScheduleQuestionnaireModal
        open={askModalOpen}
        onOpenChange={(v) => { setAskModalOpen(v); if (!v) loadAwarded(); }}
        project={project}
        user={user}
        initialContractorId={askInitialContractor}
        initialTrades={askInitialTrades}
      />
    </div>
  );
}

// =================================================================
// CASCADE SECTION — Send button + per-contractor status table
// =================================================================
function CascadeSection({ schedule, rows, loading, sending, onSend, onRefresh }) {
  const sentAt = schedule?.cascade_sent_at;
  const confirmedCount = rows.filter(r => r.status === 'confirmed').length;
  const pushedCount = rows.filter(r => r.status === 'pushed_back').length;
  const pendingCount = rows.filter(r => r.status === 'sent').length;
  const failedCount = rows.filter(r => r.status === 'failed').length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="cascade-section">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-4 h-4 text-emerald-600" /> Cascade confirmation emails
          </h3>
          <p className="text-xs text-slate-500">
            {sentAt
              ? `Last sent ${fmtMDYDateTime(sentAt)}`
              : 'Send each affected contractor their proposed window with Confirm / Push-back buttons.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            data-testid="cascade-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={onSend}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="cascade-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {sentAt ? 'Re-send to all' : 'Send cascade'}
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      {sentAt && (
        <div className="px-4 py-2 flex flex-wrap gap-2 text-[11px] font-semibold border-b border-slate-100 bg-slate-50/40">
          <Chip tone="emerald" icon={CheckCircle2} label="Confirmed" count={confirmedCount} />
          <Chip tone="amber"   icon={AlertTriangle} label="Pushed back" count={pushedCount} />
          <Chip tone="blue"    icon={Clock} label="Awaiting" count={pendingCount} />
          {failedCount > 0 && <Chip tone="rose" icon={AlertTriangle} label="Failed" count={failedCount} />}
        </div>
      )}

      {/* Rows */}
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-500 text-center">
          {sentAt
            ? 'No recipients recorded — make sure phases are assigned to contractors.'
            : 'No emails sent yet. Click Send cascade once contractors are assigned to phases.'}
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map(r => (
            <div key={r.contractor_id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`cascade-row-${r.contractor_id}`}>
              <div className="col-span-5 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{r.contractor_company || r.contractor_name || r.contractor_email}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.contractor_email}</div>
              </div>
              <div className="col-span-3 text-xs text-slate-600">
                {r.bar_count} bar{r.bar_count === 1 ? '' : 's'} · {r.phase_ids.join(', ')}
              </div>
              <div className="col-span-3 text-right">
                <StatusBadge status={r.status} />
                {r.responded_at && (
                  <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
                    {fmtMDYDateTime(r.responded_at)}
                  </div>
                )}
              </div>
              <div className="col-span-1" />
              {r.response_note && (
                <div className="col-span-12 mt-1 px-3 py-2 bg-slate-50 rounded text-xs text-slate-700 italic">
                  &ldquo;{r.response_note}&rdquo;
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:     { tone: 'slate',   label: 'Pending',     Icon: Clock },
    sent:        { tone: 'blue',    label: 'Awaiting',    Icon: Clock },
    confirmed:   { tone: 'emerald', label: 'Confirmed',   Icon: CheckCircle2 },
    pushed_back: { tone: 'amber',   label: 'Pushed back', Icon: AlertTriangle },
    failed:      { tone: 'rose',    label: 'Failed',      Icon: AlertTriangle },
  };
  const { tone, label, Icon } = map[status] || map.pending;
  const tones = {
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
    blue:    'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    rose:    'bg-rose-50 text-rose-800 border-rose-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${tones[tone]}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function Chip({ tone, icon: Icon, label, count }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    blue:    'bg-blue-50 text-blue-800 border-blue-200',
    rose:    'bg-rose-50 text-rose-800 border-rose-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tones[tone]}`}>
      <Icon className="w-3 h-3" /> {label}: {count}
    </span>
  );
}
